import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PPQ } from '@/domain/types';
import { clipEndTicks, clipLengthTicks, signatureMapOf, tempoMapOf } from '@/domain/selectors';
import { snapTick } from '@/timing/snap';
import { signatureAt, ticksPerBar, ticksToSeconds } from '@/timing/tempoMap';
import { command } from '@/commands/registry';
import * as ClipCommands from '@/commands/clipCommands';
import * as ClipGainCommands from '@/commands/clipGainCommands';
import * as TransportCommands from '@/commands/transportCommands';
import * as AutomationCommands from '@/commands/automationCommands';
import * as MidiCommands from '@/commands/midiCommands';
import * as TakeCommands from '@/commands/takeCommands';
import * as CrossfadeCommands from '@/commands/crossfadeCommands';
import { findCrossfades } from '@/domain/crossfade';
import { fadeGainAt } from '@/engine/schedulePlan';
import { getServices } from '@/state/services';
import { createId, newGainPointId } from '@/util/id';
import { useDaw } from '@/state/store';
import { canRipple, movedAnywhere } from '@/domain/ripple';
import { bareOverlaps } from '@/domain/overlap';
import type { WaveformPeaks } from '@/assets/waveformTypes';
import { drawArrangement, drawRuler } from './draw';
import {
  automationValueToY,
  clipGainArea,
  clipGainPointAt,
  laneAtY,
  laneLayout,
  tickToX,
  visibleClipRects,
  xToClipSource,
  xToTick,
  yToAutomationValue,
  yToClipGain,
  zoomAround,
  type ClipRect,
  type LaneLayout,
  type TimelineView,
} from './view';

/**
 * The arrangement view.
 *
 * All hit testing happens against the same geometry the renderer uses, so what
 * looks clickable is clickable. Drag gestures dispatch commands continuously
 * and rely on the command stack's coalescing to collapse them into a single
 * undo step when the gesture ends.
 */

const EDGE_GRAB_PX = 7;
const FADE_HANDLE_PX = 10;
/** How near a warp marker the pointer must be to grab it. */
const WARP_GRAB_PX = 5;
/** How close a click has to be to an automation point to grab it. */
const POINT_GRAB_PX = 7;
/** How close a click has to be to a crossfade's shape handle to grab it. */
const HANDLE_GRAB_PX = 8;
/** Pixels of vertical drag that take a fade from one extreme to the other. */
const SHAPE_DRAG_PX = 70;

/** Vertical drag distance, as a curvature between -1 and 1. */
function curvatureFromDrag(start: number, fromY: number, toY: number): number {
  return Math.min(1, Math.max(-1, start + (toY - fromY) / SHAPE_DRAG_PX));
}

type Gesture =
  | { kind: 'none' }
  | { kind: 'marquee'; x0: number; y0: number; x1: number; y1: number; additive: boolean }
  | {
      kind: 'move';
      clipIds: string[];
      startTick: number;
      anchorTick: number;
      appliedDelta: number;
      startLaneIndex: number;
      /** Where the dragged clip began, so a ripple knows what gap to close. */
      startTrackId: string;
    }
  | { kind: 'trim'; clipId: string; edge: 'start' | 'end' }
  | { kind: 'clipGain'; clipId: string; pointId: string; rect: ClipRect }
  | { kind: 'stretch'; clipId: string }
  | { kind: 'warpMarker'; clipId: string; markerId: string }
  | { kind: 'fade'; clipId: string; edge: 'in' | 'out' }
  | { kind: 'scrub' }
  | { kind: 'loop'; anchorTick: number }
  | { kind: 'automation'; laneId: string; pointId: string; lane: LaneLayout }
  | { kind: 'laneMarquee'; laneId: string; lane: LaneLayout; x0: number; y0: number; x1: number; y1: number }
  | {
      kind: 'movePoints';
      laneId: string;
      lane: LaneLayout;
      pointIds: string[];
      anchorTick: number;
      anchorValue: number;
      appliedTicks: number;
      appliedValue: number;
    }
  | { kind: 'comp'; lane: LaneLayout; fromX: number; toX: number }
  | { kind: 'fadeShape'; clipId: string; edge: 'in' | 'out'; startY: number; startCurvature: number }
  | {
      kind: 'crossfadeShape';
      trackId: string;
      tick: number;
      takeLane: number;
      startY: number;
      startCurvature: number;
      side: 'both' | 'out' | 'in';
    }
  | { kind: 'timeRange'; anchorTick: number; anchorY: number };

export function Timeline(): JSX.Element {
  const project = useDaw((s) => s.project);
  const selection = useDaw((s) => s.selection);
  const editTool = useDaw((s) => s.editTool);
  const dispatch = useDaw((s) => s.dispatch);
  const setView = useDaw((s) => s.setView);
  const selectClips = useDaw((s) => s.selectClips);
  const setSelection = useDaw((s) => s.setSelection);
  const setRange = useDaw((s) => s.setRange);
  const seekTicks = useDaw((s) => s.seekTicks);
  const importFiles = useDaw((s) => s.importFiles);
  const importMidiFiles = useDaw((s) => s.importMidiFiles);

  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rulerRef = useRef<HTMLCanvasElement>(null);
  const playheadRef = useRef<HTMLDivElement>(null);
  const gestureRef = useRef<Gesture>({ kind: 'none' });

  const [size, setSize] = useState({ width: 0, height: 0 });
  const [rulerHeight, setRulerHeight] = useState(30);
  const [marquee, setMarquee] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
  const [compRange, setCompRange] = useState<{ lane: LaneLayout; fromX: number; toX: number } | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [peaksVersion, setPeaksVersion] = useState(0);

  const clipGainEditing = useDaw((s) => s.clipGainEditing);
  const services = getServices();
  const selectedClipIds = useMemo(() => new Set(selection.clipIds), [selection.clipIds]);
  const selectedPointIds = useMemo(() => new Set(selection.pointIds), [selection.pointIds]);
  const sigMap = useMemo(() => signatureMapOf(project), [project]);

  const view: TimelineView = useMemo(
    () => ({
      pixelsPerBeat: project.view.pixelsPerBeat,
      scrollTicks: project.view.scrollTicks,
      scrollTop: project.view.scrollTop,
      ppq: project.transport.ppq,
      width: size.width,
      height: size.height,
    }),
    [project.view.pixelsPerBeat, project.view.scrollTicks, project.view.scrollTop, project.transport.ppq, size],
  );

  // -- sizing --------------------------------------------------------------

  useEffect(() => {
    const element = wrapRef.current;
    if (!element) return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0].contentRect;
      setSize({ width: Math.floor(rect.width), height: Math.floor(rect.height) });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const element = rulerRef.current?.parentElement;
    if (!element) return;
    const observer = new ResizeObserver((entries) => setRulerHeight(Math.floor(entries[0].contentRect.height)));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // -- waveform loading ----------------------------------------------------

  /**
   * Requests peaks for anything on screen that has none yet, then triggers one
   * redraw when they arrive. Drawing itself stays synchronous; it reads only
   * what is already in memory.
   */
  useEffect(() => {
    const assets = services.assets();
    if (!assets) return;
    let cancelled = false;
    const needed = new Set<string>();
    for (const clip of Object.values(project.clips)) {
      if (clip.kind === 'audio' && !assets.peekPeaks(clip.assetId)) needed.add(clip.assetId);
    }
    if (needed.size === 0) return;
    void Promise.all([...needed].map((id) => assets.getPeaks(id))).then(() => {
      if (!cancelled) setPeaksVersion((v) => v + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [project.clips, services, peaksVersion]);

  const peaksFor = useCallback(
    (assetId: string): WaveformPeaks | null => services.assets()?.peekPeaks(assetId) ?? null,
    [services],
  );

  // -- drawing -------------------------------------------------------------

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || size.width === 0) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = Math.floor(size.width * dpr);
    canvas.height = Math.floor(size.height * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    drawArrangement({
      ctx,
      project,
      view,
      selectedClipIds,
      selectedTrackId: selection.trackId,
      selectedPointIds,
      peaksFor,
      marquee,
      compRange,
      timeRange: selection.range,
      clipGainEditing,
      // Read from the gesture rather than held in state, because a drag
      // redraws on every pointer event anyway and a second copy of "which
      // point" would only need keeping in step.
      draggingGainPointId: gestureRef.current.kind === 'clipGain' ? gestureRef.current.pointId : null,
      dpr,
    });
  }, [
    project,
    view,
    selectedClipIds,
    selectedPointIds,
    selection.trackId,
    selection.range,
    marquee,
    compRange,
    clipGainEditing,
    size,
    peaksFor,
    peaksVersion,
  ]);

  useEffect(() => {
    const canvas = rulerRef.current;
    if (!canvas || size.width === 0) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = Math.floor(size.width * dpr);
    canvas.height = Math.floor(rulerHeight * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    drawRuler(ctx, project, { ...view, height: rulerHeight }, dpr);
  }, [project, view, size.width, rulerHeight]);

  // -- playhead ------------------------------------------------------------

  useEffect(() => {
    const element = playheadRef.current;
    if (!element) return;
    const paint = (ticks: number): void => {
      const x = tickToX(view, ticks);
      element.style.transform = `translateX(${x}px)`;
      element.style.opacity = x < -2 || x > view.width + 2 ? '0' : '1';
    };
    paint(useDaw.getState().project.transport.playheadTicks);
    return services.playhead.subscribe(paint);
  }, [services, view]);

  // -- gestures ------------------------------------------------------------

  const snap = useCallback(
    (tick: number) => snapTick(sigMap, project.transport.snap, tick),
    [sigMap, project.transport.snap],
  );

  const localPoint = (
    event: { clientX: number; clientY: number },
    element: HTMLElement,
  ): { x: number; y: number } => {
    const rect = element.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  /**
   * The warp marker under the pointer, if any.
   *
   * Checked before the clip's own zones, because a marker sitting near an edge
   * has to win: it is the smaller, more precise target, and the edge behind it
   * can always be grabbed a few pixels away.
   */
  const hitWarpMarker = useCallback(
    (x: number, y: number): { clipId: string; markerId: string } | null => {
      const { lanes } = laneLayout(project);
      const rects = visibleClipRects(project, view, lanes);
      for (let i = rects.length - 1; i >= 0; i--) {
        const rect = rects[i];
        if (x < rect.x || x > rect.x + rect.width || y < rect.y || y > rect.y + rect.height) continue;
        const clip = project.clips[rect.clipId];
        if (clip?.kind !== 'audio' || clip.warpMarkers.length === 0) continue;
        for (const marker of clip.warpMarkers) {
          const at = clip.startTicks + marker.beat * project.transport.ppq;
          if (Math.abs(tickToX(view, at) - x) <= WARP_GRAB_PX) {
            return { clipId: clip.id, markerId: marker.id };
          }
        }
      }
      return null;
    },
    [project, view],
  );

  const hitTest = useCallback(
    (x: number, y: number): { rect: ClipRect; zone: 'body' | 'start' | 'end' | 'fadeIn' | 'fadeOut' } | null => {
      const { lanes } = laneLayout(project);
      const rects = visibleClipRects(project, view, lanes);
      for (let i = rects.length - 1; i >= 0; i--) {
        const rect = rects[i];
        if (x < rect.x || x > rect.x + rect.width || y < rect.y || y > rect.y + rect.height) continue;
        const fromLeft = x - rect.x;
        const fromRight = rect.x + rect.width - x;
        const inHeader = y - rect.y < 16;
        if (inHeader && fromLeft <= FADE_HANDLE_PX && rect.width > 30) return { rect, zone: 'fadeIn' };
        if (inHeader && fromRight <= FADE_HANDLE_PX && rect.width > 30) return { rect, zone: 'fadeOut' };
        if (fromLeft <= EDGE_GRAB_PX) return { rect, zone: 'start' };
        if (fromRight <= EDGE_GRAB_PX) return { rect, zone: 'end' };
        return { rect, zone: 'body' };
      }
      return null;
    },
    [project, view],
  );

  /** Every track whose lane the pointer passed between two vertical positions. */
  const tracksBetween = useCallback(
    (fromY: number, toY: number): string[] => {
      const top = Math.min(fromY, toY) + view.scrollTop;
      const bottom = Math.max(fromY, toY) + view.scrollTop;
      const ids: string[] = [];
      for (const lane of laneLayout(project).lanes) {
        if (lane.laneId !== null || lane.takeLane !== null) continue;
        if (lane.top + lane.height <= top || lane.top >= bottom + 1) continue;
        ids.push(lane.trackId);
      }
      // A drag that never left one lane still selects that lane.
      return ids.length > 0 ? ids : project.trackOrder.slice(0, 1);
    },
    [project, view.scrollTop],
  );

  /** The automation point under a screen position, if any. */
  const hitAutomationPoint = useCallback(
    (laneLayoutEntry: LaneLayout, x: number, y: number): string | null => {
      const lane = laneLayoutEntry.laneId ? project.automation[laneLayoutEntry.laneId] : null;
      if (!lane) return null;
      let bestId: string | null = null;
      let bestDistance = POINT_GRAB_PX;
      for (const point of lane.points) {
        const px = tickToX(view, point.tick);
        const py = automationValueToY(laneLayoutEntry, point.value, view.scrollTop);
        const distance = Math.hypot(px - x, py - y);
        if (distance <= bestDistance) {
          bestDistance = distance;
          bestId = point.id;
        }
      }
      return bestId;
    },
    [project.automation, view],
  );

  /**
   * Pointer handling inside an automation lane.
   *
   * Clicking a point grabs it; clicking anywhere else creates a point there and
   * grabs that, so drawing a curve is one gesture rather than click-then-drag.
   * Alt-click deletes, which is the convention every DAW shares.
   */
  const beginAutomationGesture = (
    laneLayoutEntry: LaneLayout,
    x: number,
    y: number,
    altKey: boolean,
    shiftKey: boolean,
  ): void => {
    const laneId = laneLayoutEntry.laneId;
    if (!laneId) return;
    const tick = Math.max(0, Math.round(snap(xToTick(view, x))));
    const value = yToAutomationValue(laneLayoutEntry, y, view.scrollTop);
    const existing = hitAutomationPoint(laneLayoutEntry, x, y);

    setSelection({ trackId: laneLayoutEntry.trackId, laneId, clipIds: [] });

    // Shift is what a lane has left: plain drag draws a point, alt deletes one,
    // so selecting a stretch needs its own modifier.
    if (shiftKey) {
      gestureRef.current = { kind: 'laneMarquee', laneId, lane: laneLayoutEntry, x0: x, y0: y, x1: x, y1: y };
      setMarquee({ x0: x, y0: y, x1: x, y1: y });
      return;
    }

    // Dragging inside an existing selection moves the whole of it, which is what
    // anyone expects after taking the trouble to select several points.
    if (existing && selectedPointIds.has(existing) && selection.pointIds.length > 1) {
      gestureRef.current = {
        kind: 'movePoints',
        laneId,
        lane: laneLayoutEntry,
        pointIds: [...selection.pointIds],
        anchorTick: tick,
        anchorValue: value,
        appliedTicks: 0,
        appliedValue: 0,
      };
      return;
    }

    if (existing && altKey) {
      dispatch(command(AutomationCommands.deleteAutomationPoints, { laneId, pointIds: [existing] }));
      return;
    }

    if (existing) {
      setSelection({ pointIds: [existing] });
      gestureRef.current = { kind: 'automation', laneId, pointId: existing, lane: laneLayoutEntry };
      return;
    }

    if (altKey) return;

    const pointId = createId('pnt');
    dispatch({
      type: AutomationCommands.addAutomationPoint.type,
      params: { laneId, pointId, tick, value },
    });
    setSelection({ pointIds: [pointId] });
    gestureRef.current = { kind: 'automation', laneId, pointId, lane: laneLayoutEntry };
  };

  /** The crossfade handle under a position, if the pointer is on one. */
  const hitCrossfadeHandle = useCallback(
    (lane: LaneLayout, x: number, y: number): { trackId: string; tick: number; curvature: number } | null => {
      const takeLane = lane.takeLane ?? 0;
      for (const region of findCrossfades(project, lane.trackId, takeLane)) {
        const x0 = tickToX(view, region.startTicks);
        const x1 = tickToX(view, region.endTicks);
        const incoming = project.clips[region.incomingId];
        if (incoming?.kind !== 'audio') continue;

        const top = lane.top - view.scrollTop + 2;
        const bottom = lane.top - view.scrollTop + lane.height - 4;
        const midX = (x0 + x1) / 2;
        const midY = bottom - (bottom - top) * fadeGainAt(incoming.fadeIn.curve, 0.5, 1, true, incoming.fadeIn.curvature);
        if (Math.hypot(midX - x, midY - y) <= HANDLE_GRAB_PX) {
          return { trackId: lane.trackId, tick: region.startTicks, curvature: incoming.fadeIn.curvature };
        }
      }
      return null;
    },
    [project, view],
  );

  const onCanvasPointerDown = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    if (event.button !== 0) return;
    const canvas = event.currentTarget;
    canvas.setPointerCapture(event.pointerId);
    const { x, y } = localPoint(event, canvas);
    const tick = xToTick(view, x);
    const additive = event.shiftKey || event.metaKey || event.ctrlKey;

    // The range tool takes precedence everywhere: with it selected, a drag is
    // always marking time, never grabbing a clip.
    if (editTool === 'range') {
      gestureRef.current = { kind: 'timeRange', anchorTick: tick, anchorY: y };
      setRange({
        startTicks: Math.max(0, Math.round(snap(tick))),
        endTicks: Math.max(0, Math.round(snap(tick))) + 1,
        trackIds: tracksBetween(y, y),
      });
      return;
    }

    // Automation lanes own their whole row: no clip can be there, so they are
    // resolved before clip hit testing rather than after it.
    const lane = laneAtY(laneLayout(project).lanes, y + view.scrollTop);
    if (lane?.laneId) {
      beginAutomationGesture(lane, x, y, event.altKey, event.shiftKey);
      return;
    }

    // A drag across a take lane comps that range in; a plain click comps the
    // whole clip under the pointer. Take lanes are not for editing — the take
    // stays as recorded, and the comp above it is what changes.
    if (lane?.takeLane) {
      const hitTake = hitTest(x, y);
      if (event.altKey && hitTake) {
        dispatch({ type: TakeCommands.setClipTakeLane.type, params: { clipIds: [hitTake.rect.clipId], takeLane: 0 } });
        return;
      }
      gestureRef.current = { kind: 'comp', lane, fromX: x, toX: x };
      setCompRange({ lane, fromX: x, toX: x });
      return;
    }

    // The crossfade handle sits on top of both clips, so it is tested first.
    if (lane && lane.laneId === null) {
      const handle = hitCrossfadeHandle(lane, x, y);
      if (handle) {
        // Alt bends one half only — whichever the pointer is on. It gives up
        // the constant level across the join, which is why it takes a modifier
        // rather than being what a plain drag does.
        const side = !event.altKey ? 'both' : x < tickToX(view, handle.tick) ? 'out' : 'in';
        gestureRef.current = {
          kind: 'crossfadeShape',
          trackId: handle.trackId,
          tick: handle.tick,
          takeLane: lane.takeLane ?? 0,
          startY: y,
          startCurvature: handle.curvature,
          side,
        };
        return;
      }
    }

    // A warp marker is grabbed before anything else on the clip.
    const marker = editTool === 'select' ? hitWarpMarker(x, y) : null;
    if (marker) {
      if (event.altKey) {
        dispatch(command(ClipCommands.removeWarpMarker, marker));
        return;
      }
      setSelection({ clipIds: [marker.clipId] });
      gestureRef.current = { kind: 'warpMarker', ...marker };
      return;
    }

    const hit = hitTest(x, y);

    if (!hit) {
      if (!additive) setSelection({ clipIds: [], pointIds: [] });
      if (lane) setSelection({ trackId: lane.trackId, laneId: null });
      gestureRef.current = { kind: 'marquee', x0: x, y0: y, x1: x, y1: y, additive };
      setMarquee({ x0: x, y0: y, x1: x, y1: y });
      return;
    }

    if (editTool === 'split') {
      dispatch(command(ClipCommands.splitClips, { clipIds: [hit.rect.clipId], tick: Math.round(snap(tick)) }));
      return;
    }

    // With the envelope tool on, the clip body is a level ride rather than
    // something to drag: a point under the pointer is grabbed, alt removes it,
    // and anywhere else adds one where it was clicked.
    const gainClip = clipGainEditing ? project.clips[hit.rect.clipId] : undefined;
    if (gainClip?.kind === 'audio' && hit.zone === 'body') {
      setSelection({ trackId: hit.rect.trackId, clipIds: [gainClip.id] });
      const existing = clipGainPointAt(project, view, gainClip, hit.rect, x, y);

      if (existing && event.altKey) {
        dispatch(command(ClipGainCommands.removeClipGainPoints, { clipId: gainClip.id, pointIds: [existing] }));
        return;
      }

      const pointId = existing ?? newGainPointId();
      if (!existing) {
        dispatch(
          command(ClipGainCommands.addClipGainPoint, {
            clipId: gainClip.id,
            pointId,
            sourceSec: xToClipSource(project, view, gainClip, x),
            gain: yToClipGain(clipGainArea(hit.rect), y),
            curve: 'linear' as const,
          }),
        );
      }
      gestureRef.current = { kind: 'clipGain', clipId: gainClip.id, pointId, rect: hit.rect };
      return;
    }

    setSelection({ trackId: hit.rect.trackId });
    const alreadySelected = selectedClipIds.has(hit.rect.clipId);
    if (!alreadySelected) selectClips([hit.rect.clipId], additive);

    if (hit.zone === 'start' || hit.zone === 'end') {
      const clip = project.clips[hit.rect.clipId];
      if (event.altKey && hit.zone === 'end' && clip?.kind === 'audio') {
        // Alt turns the trim handle into a stretch handle: the same grip, but
        // the clip's whole source is fitted to the new length instead of being
        // cut short. It is the end edge only, because stretching from the front
        // would move the clip's start as well as its length, and a gesture that
        // does two things at once is one no one trusts.
        gestureRef.current = { kind: 'stretch', clipId: hit.rect.clipId };
        return;
      }
      gestureRef.current = { kind: 'trim', clipId: hit.rect.clipId, edge: hit.zone };
      return;
    }
    if (hit.zone === 'fadeIn' || hit.zone === 'fadeOut') {
      const edge = hit.zone === 'fadeIn' ? 'in' : 'out';
      const clip = project.clips[hit.rect.clipId];
      if (event.altKey && clip?.kind === 'audio') {
        // Alt turns the fade handle into a shape handle: same grip, dragged
        // vertically, which is how the length and the curve stay one control.
        gestureRef.current = {
          kind: 'fadeShape',
          clipId: hit.rect.clipId,
          edge,
          startY: y,
          startCurvature: (edge === 'in' ? clip.fadeIn : clip.fadeOut).curvature,
        };
        return;
      }
      gestureRef.current = { kind: 'fade', clipId: hit.rect.clipId, edge };
      return;
    }

    const clipIds = alreadySelected || additive ? [...new Set([...selection.clipIds, hit.rect.clipId])] : [hit.rect.clipId];
    const lanes = laneLayout(project).lanes;
    gestureRef.current = {
      kind: 'move',
      clipIds,
      startTick: tick,
      anchorTick: project.clips[hit.rect.clipId]?.startTicks ?? 0,
      appliedDelta: 0,
      startLaneIndex: lanes.findIndex((l) => l.trackId === hit.rect.trackId),
      startTrackId: hit.rect.trackId,
    };
  };

  const onCanvasPointerMove = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    const gesture = gestureRef.current;
    const canvas = event.currentTarget;
    const { x, y } = localPoint(event, canvas);

    if (gesture.kind === 'none') {
      const lane = laneAtY(laneLayout(project).lanes, y + view.scrollTop);
      if (lane?.laneId) {
        canvas.style.cursor = hitAutomationPoint(lane, x, y) ? 'grab' : 'crosshair';
        return;
      }
      if (lane?.takeLane) {
        canvas.style.cursor = 'text';
        return;
      }
      if (lane && lane.laneId === null && hitCrossfadeHandle(lane, x, y)) {
        canvas.style.cursor = 'ns-resize';
        return;
      }
      if (editTool === 'select' && hitWarpMarker(x, y)) {
        canvas.style.cursor = 'col-resize';
        return;
      }
      const hit = hitTest(x, y);
      canvas.style.cursor =
        editTool === 'range'
          ? 'text'
          : editTool === 'split'
          ? 'col-resize'
          : hit?.zone === 'start' || hit?.zone === 'end'
            ? 'ew-resize'
            : hit?.zone === 'fadeIn' || hit?.zone === 'fadeOut'
              ? 'crosshair'
              : hit
                ? 'grab'
                : 'default';
      return;
    }

    const tick = xToTick(view, x);

    if (gesture.kind === 'timeRange') {
      const from = Math.max(0, Math.round(snap(Math.min(gesture.anchorTick, tick))));
      const to = Math.max(0, Math.round(snap(Math.max(gesture.anchorTick, tick))));
      setRange({
        startTicks: from,
        endTicks: Math.max(from + 1, to),
        trackIds: tracksBetween(gesture.anchorY, y),
      });
      return;
    }

    if (gesture.kind === 'fadeShape') {
      dispatch({
        type: ClipCommands.setClipFade.type,
        params: {
          clipId: gesture.clipId,
          edge: gesture.edge,
          curvature: curvatureFromDrag(gesture.startCurvature, gesture.startY, y),
        },
      });
      return;
    }

    if (gesture.kind === 'crossfadeShape') {
      dispatch({
        type: CrossfadeCommands.setCrossfadeShape.type,
        params: {
          trackId: gesture.trackId,
          tick: gesture.tick,
          takeLane: gesture.takeLane,
          curvature: curvatureFromDrag(gesture.startCurvature, gesture.startY, y),
          side: gesture.side,
        },
      });
      return;
    }

    if (gesture.kind === 'comp') {
      gestureRef.current = { ...gesture, toX: x };
      setCompRange({ lane: gesture.lane, fromX: gesture.fromX, toX: x });
      return;
    }

    if (gesture.kind === 'marquee') {
      gestureRef.current = { ...gesture, x1: x, y1: y };
      setMarquee({ x0: gesture.x0, y0: gesture.y0, x1: x, y1: y });
      return;
    }

    if (gesture.kind === 'laneMarquee') {
      gestureRef.current = { ...gesture, x1: x, y1: y };
      setMarquee({ x0: gesture.x0, y0: gesture.y0, x1: x, y1: y });
      return;
    }

    if (gesture.kind === 'movePoints') {
      // Deltas are applied incrementally against what has already been sent, so
      // a drag is one coalesced command rather than a fight between absolute
      // positions and the clamping the command does.
      const wantedTicks = Math.round(snap(tick) - gesture.anchorTick);
      const wantedValue = yToAutomationValue(gesture.lane, y, view.scrollTop) - gesture.anchorValue;
      const deltaTicks = wantedTicks - gesture.appliedTicks;
      const deltaValue = wantedValue - gesture.appliedValue;
      if (deltaTicks === 0 && Math.abs(deltaValue) < 1e-4) return;
      dispatch(
        command(AutomationCommands.moveAutomationPoints, {
          laneId: gesture.laneId,
          pointIds: gesture.pointIds,
          deltaTicks,
          deltaValue,
        }),
      );
      gestureRef.current = { ...gesture, appliedTicks: wantedTicks, appliedValue: wantedValue };
      return;
    }

    if (gesture.kind === 'move') {
      // Snap the clip's own start rather than the raw pointer delta, so a clip
      // that began off-grid lands on the grid instead of staying off it.
      const rawDelta = tick - gesture.startTick;
      const target = snap(gesture.anchorTick + rawDelta);
      const wantedDelta = target - gesture.anchorTick;
      const increment = Math.round(wantedDelta - gesture.appliedDelta);

      const lanes = laneLayout(project).lanes;
      const laneIndex = lanes.findIndex((l) => {
        const top = l.top - view.scrollTop;
        return y >= top && y < top + l.height;
      });
      const laneShift = laneIndex >= 0 ? laneIndex - gesture.startLaneIndex : 0;

      let targetTrackIds: string[] | null = null;
      if (laneShift !== 0) {
        targetTrackIds = gesture.clipIds.map((clipId) => {
          const currentIndex = lanes.findIndex((l) => l.trackId === project.clips[clipId]?.trackId);
          const next = lanes[Math.min(lanes.length - 1, Math.max(0, currentIndex + laneShift))];
          return next?.trackId ?? project.clips[clipId]?.trackId ?? '';
        });
      }

      if (increment !== 0 || targetTrackIds) {
        dispatch(
          command(ClipCommands.moveClips, {
            clipIds: gesture.clipIds,
            deltaTicks: increment,
            targetTrackIds,
          }),
        );
        gestureRef.current = {
          ...gesture,
          appliedDelta: gesture.appliedDelta + increment,
          startLaneIndex: targetTrackIds ? laneIndex : gesture.startLaneIndex,
        };
      }
      return;
    }

    if (gesture.kind === 'automation') {
      dispatch(
        command(AutomationCommands.moveAutomationPoint, {
          laneId: gesture.laneId,
          pointId: gesture.pointId,
          tick: Math.max(0, Math.round(snap(tick))),
          value: yToAutomationValue(gesture.lane, y, view.scrollTop),
        }),
      );
      return;
    }

    if (gesture.kind === 'clipGain') {
      const clip = project.clips[gesture.clipId];
      if (clip?.kind !== 'audio') return;
      dispatch(
        command(ClipGainCommands.moveClipGainPoint, {
          clipId: gesture.clipId,
          pointId: gesture.pointId,
          sourceSec: xToClipSource(project, view, clip, x),
          gain: yToClipGain(clipGainArea(gesture.rect), y),
        }),
      );
      return;
    }

    if (gesture.kind === 'trim') {
      dispatch(
        command(ClipCommands.trimClip, {
          clipId: gesture.clipId,
          edge: gesture.edge,
          toTick: Math.max(0, Math.round(snap(tick))),
          ripple: useDaw.getState().rippleEditing,
        }),
      );
      return;
    }

    if (gesture.kind === 'warpMarker') {
      const clip = project.clips[gesture.clipId];
      if (!clip || clip.kind !== 'audio') return;
      const beat = (Math.round(snap(tick)) - clip.startTicks) / project.transport.ppq;
      dispatch(
        command(ClipCommands.moveWarpMarker, { clipId: gesture.clipId, markerId: gesture.markerId, beat }),
      );
      return;
    }

    if (gesture.kind === 'stretch') {
      const clip = project.clips[gesture.clipId];
      if (!clip || clip.kind !== 'audio') return;
      const map = tempoMapOf(project);
      const targetSec =
        ticksToSeconds(map, Math.max(clip.startTicks + 1, Math.round(snap(tick)))) -
        ticksToSeconds(map, clip.startTicks);
      if (targetSec <= 0) return;
      dispatch(command(ClipCommands.fitClipToLength, { clipId: gesture.clipId, targetSec }));
      return;
    }

    if (gesture.kind === 'fade') {
      const clip = project.clips[gesture.clipId];
      if (!clip || clip.kind !== 'audio') return;
      const lengthTicks = clipLengthTicks(project, clip);
      const fraction =
        gesture.edge === 'in'
          ? (tick - clip.startTicks) / lengthTicks
          : (clip.startTicks + lengthTicks - tick) / lengthTicks;
      dispatch(
        command(ClipCommands.setClipFade, {
          clipId: gesture.clipId,
          edge: gesture.edge,
          durationSec: Math.max(0, Math.min(1, fraction)) * clip.durationSec,
          curve: null,
          curvature: null,
        }),
      );
    }
  };

  /**
   * Closes the gap behind dragged clips, once the drag has finished.
   *
   * A drag is uniform — every clip moved by the same number of ticks and the same
   * number of lanes — so where each one came from is recovered by undoing that,
   * rather than by recording a position per clip while the pointer is moving.
   *
   * A selection ripples as a block, so it has to be one: if an unselected clip
   * starts inside the stretch the selection covers, closing that stretch would
   * drag material the user did not select. The drag then stands as an ordinary
   * move — which is what it was — and the toast says why, because a ripple
   * button that silently does nothing is worse than one that explains itself.
   */
  const rippleAfterMove = (gesture: Extract<Gesture, { kind: 'move' }>): void => {
    if (!useDaw.getState().rippleEditing) return;

    const project = useDaw.getState().project;
    const { lanes } = laneLayout(project);
    const laneOf = (trackId: string | undefined): number =>
      lanes.findIndex((lane) => lane.trackId === trackId);
    // The drag moved every clip by the same number of lanes, so where each one
    // started is that many lanes back from where it is now.
    const laneShift = laneOf(project.clips[gesture.clipIds[0]]?.trackId) - gesture.startLaneIndex;
    const moved = gesture.clipIds.flatMap((clipId) => {
      const clip = project.clips[clipId];
      if (!clip) return [];
      const fromLane = laneOf(clip.trackId) - laneShift;
      const fromTrackId = lanes[fromLane]?.trackId;
      if (!fromTrackId) return [];
      return [{ clipId, fromTrackId, fromStartTicks: Math.max(0, clip.startTicks - gesture.appliedDelta) }];
    });

    if (!movedAnywhere(project, moved)) return;
    if (!canRipple(project, moved)) {
      useDaw.getState().notify({
        kind: 'info',
        title: 'Moved without rippling',
        detail:
          'Ripple moves a selection as one block, and something not selected sits inside the stretch it covers. ' +
          'Select that too, or turn ripple off.',
        sticky: false,
      });
      return;
    }

    dispatch(command(ClipCommands.rippleMovedClips, { moved }));
  };

  /**
   * Crossfades whatever the edit just left overlapping, if that is switched on.
   *
   * The command is asked for unconditionally when the mode is on, and does
   * nothing when there is nothing to do, so this does not need to know whether
   * the drag actually created an overlap — only the project does.
   */
  const fadeOverlapsAfterEdit = (): void => {
    if (!useDaw.getState().autoFadeOverlaps) return;
    if (bareOverlaps(useDaw.getState().project).length === 0) return;
    dispatch(command(CrossfadeCommands.fadeBareOverlaps, { trackIds: null, curve: null }));
  };

  const onCanvasPointerUp = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    const gesture = gestureRef.current;
    gestureRef.current = { kind: 'none' };
    event.currentTarget.releasePointerCapture(event.pointerId);

    // Before the coalescing window is closed, so the ripple joins the drag's own
    // undo entry rather than becoming a second thing to undo.
    if (gesture.kind === 'move') rippleAfterMove(gesture);
    // Likewise for the fade: an edit that leaves two clips on top of each other
    // and the fix for it are one gesture, not two.
    if (gesture.kind === 'move' || gesture.kind === 'trim') fadeOverlapsAfterEdit();

    getServices().commands.breakCoalescing();

    if (gesture.kind === 'laneMarquee') {
      setMarquee(null);
      const lane = project.automation[gesture.laneId];
      if (!lane) return;

      const x0 = Math.min(gesture.x0, gesture.x1);
      const x1 = Math.max(gesture.x0, gesture.x1);
      const y0 = Math.min(gesture.y0, gesture.y1);
      const y1 = Math.max(gesture.y0, gesture.y1);

      // A marquee is horizontal by nature here: a lane is a curve over time, and
      // selecting "these points, but only the low ones" is not a thing anyone
      // wants. A tall enough drag therefore takes everything in its span.
      const wholeHeight = y1 - y0 > gesture.lane.height * 0.6;
      const fromTick = xToTick(view, x0);
      const toTick = xToTick(view, x1);

      const caught = lane.points.filter((point) => {
        if (point.tick < fromTick || point.tick > toTick) return false;
        if (wholeHeight) return true;
        const pointY = automationValueToY(gesture.lane, point.value, view.scrollTop);
        return pointY >= y0 - 4 && pointY <= y1 + 4;
      });
      setSelection({ laneId: gesture.laneId, pointIds: caught.map((point) => point.id) });
      return;
    }

    if (gesture.kind === 'comp') {
      setCompRange(null);
      const takeLane = gesture.lane.takeLane;
      if (!takeLane) return;

      const fromTick = Math.round(snap(xToTick(view, Math.min(gesture.fromX, gesture.toX))));
      const toTick = Math.round(snap(xToTick(view, Math.max(gesture.fromX, gesture.toX))));

      if (Math.abs(gesture.toX - gesture.fromX) < 4) {
        // A click, not a drag: comp in the whole clip that was clicked.
        const hit = hitTest(gesture.fromX, gesture.lane.top - view.scrollTop + gesture.lane.height / 2);
        const clip = hit ? project.clips[hit.rect.clipId] : null;
        if (clip) {
          dispatch(
            command(TakeCommands.promoteTake, {
              trackId: gesture.lane.trackId,
              takeLane,
              startTicks: clip.startTicks,
              endTicks: Math.round(clipEndTicks(project, clip)),
            }),
          );
        }
        return;
      }

      if (toTick > fromTick) {
        dispatch(
          command(TakeCommands.promoteTake, {
            trackId: gesture.lane.trackId,
            takeLane,
            startTicks: fromTick,
            endTicks: toTick,
          }),
        );
      }
      return;
    }

    if (gesture.kind === 'marquee') {
      setMarquee(null);
      const x0 = Math.min(gesture.x0, gesture.x1);
      const x1 = Math.max(gesture.x0, gesture.x1);
      const y0 = Math.min(gesture.y0, gesture.y1);
      const y1 = Math.max(gesture.y0, gesture.y1);
      if (x1 - x0 < 3 && y1 - y0 < 3) return;

      const { lanes } = laneLayout(project);
      const hits = visibleClipRects(project, view, lanes)
        .filter((r) => r.x < x1 && r.x + r.width > x0 && r.y < y1 && r.y + r.height > y0)
        .map((r) => r.clipId);
      selectClips(hits, gesture.additive);
    }
  };

  // -- ruler ---------------------------------------------------------------

  const onRulerPointerDown = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    if (event.button !== 0) return;
    const canvas = event.currentTarget;
    canvas.setPointerCapture(event.pointerId);
    const { x, y } = localPoint(event, canvas);
    const tick = Math.max(0, Math.round(snap(xToTick(view, x))));

    // The top strip sets the loop region; the rest scrubs.
    if (y < 9 || event.altKey) {
      gestureRef.current = { kind: 'loop', anchorTick: tick };
      dispatch(command(TransportCommands.setLoopRegion, { startTicks: tick, endTicks: tick + PPQ, enabled: true }));
      return;
    }
    gestureRef.current = { kind: 'scrub' };
    seekTicks(tick);
  };

  const onRulerPointerMove = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    const gesture = gestureRef.current;
    if (gesture.kind !== 'scrub' && gesture.kind !== 'loop') return;
    const { x } = localPoint(event, event.currentTarget);
    const tick = Math.max(0, Math.round(snap(xToTick(view, x))));
    if (gesture.kind === 'scrub') seekTicks(tick);
    else if (Math.abs(tick - gesture.anchorTick) > 1) {
      dispatch(
        command(TransportCommands.setLoopRegion, {
          startTicks: Math.min(gesture.anchorTick, tick),
          endTicks: Math.max(gesture.anchorTick, tick),
          enabled: true,
        }),
      );
    }
  };

  const onRulerPointerUp = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    gestureRef.current = { kind: 'none' };
    event.currentTarget.releasePointerCapture(event.pointerId);
    getServices().commands.breakCoalescing();
  };

  // -- wheel ---------------------------------------------------------------

  useEffect(() => {
    const element = wrapRef.current;
    if (!element) return;

    const onWheel = (event: WheelEvent): void => {
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        const rect = element.getBoundingClientRect();
        const factor = Math.pow(1.0022, -event.deltaY);
        setView(zoomAround(view, factor, event.clientX - rect.left));
        return;
      }
      if (event.shiftKey || Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
        event.preventDefault();
        const delta = (event.shiftKey ? event.deltaY : event.deltaX) / view.pixelsPerBeat;
        setView({ scrollTicks: Math.max(0, view.scrollTicks + delta * view.ppq) });
        return;
      }
      const { totalHeight } = laneLayout(project);
      const maxScroll = Math.max(0, totalHeight - size.height + 40);
      const next = Math.min(maxScroll, Math.max(0, view.scrollTop + event.deltaY));
      if (next !== view.scrollTop) {
        event.preventDefault();
        setView({ scrollTop: next });
      }
    };

    element.addEventListener('wheel', onWheel, { passive: false });
    return () => element.removeEventListener('wheel', onWheel);
  }, [view, setView, project, size.height]);

  // -- file drop -----------------------------------------------------------

  const onDrop = (event: React.DragEvent): void => {
    event.preventDefault();
    setDragOver(false);

    const dropped = [...event.dataTransfer.files];
    // A `.mid` is a score, not a sound: it becomes instrument tracks rather
    // than an audio clip, so the two kinds are separated before importing.
    const midi = dropped.filter((f) => /\.midi?$/i.test(f.name) || f.type === 'audio/midi');
    const audio = dropped.filter(
      (f) => !midi.includes(f) && (f.type.startsWith('audio/') || /\.(wav|mp3|m4a|aac|ogg|oga|flac)$/i.test(f.name)),
    );
    if (midi.length === 0 && audio.length === 0) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const at = Math.max(0, Math.round(snap(xToTick(view, x))));

    if (midi.length > 0) void importMidiFiles(midi, at);
    if (audio.length > 0) {
      const lane = laneAtY(laneLayout(project).lanes, y + view.scrollTop);
      void importFiles(audio, lane?.trackId ?? null, at);
    }
  };

  const isEmpty = Object.keys(project.clips).length === 0;

  return (
    <div className="timeline">
      <div className="timeline__ruler">
        <canvas
          ref={rulerRef}
          style={{ width: '100%', height: '100%' }}
          onPointerDown={onRulerPointerDown}
          onPointerMove={onRulerPointerMove}
          onPointerUp={onRulerPointerUp}
          aria-label="Timeline ruler. Drag to move the playhead; drag the top strip to set the loop region."
        />
      </div>
      <div
        ref={wrapRef}
        className="timeline__canvas-wrap"
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
      >
        <canvas
          ref={canvasRef}
          onPointerDown={onCanvasPointerDown}
          onPointerMove={onCanvasPointerMove}
          onPointerUp={onCanvasPointerUp}
          onPointerCancel={onCanvasPointerUp}
          onDoubleClick={(e) => {
            const { x, y } = localPoint(e, e.currentTarget);
            const hit = hitTest(x, y);
            if (hit) {
              dispatch(
                command(ClipCommands.setClipMuted, {
                  clipIds: [hit.rect.clipId],
                  muted: !project.clips[hit.rect.clipId]?.muted,
                }),
              );
              return;
            }
            // Double-clicking empty space on an instrument track writes a bar of
            // MIDI there and selects it, which opens the editor.
            const lane = laneAtY(laneLayout(project).lanes, y + view.scrollTop);
            const track = lane && lane.laneId === null ? project.tracks[lane.trackId] : null;
            if (!track || track.kind !== 'instrument') return;
            const sig = signatureAt(sigMap, xToTick(view, x));
            const bar = ticksPerBar(sig, sigMap.ppq);
            const startTicks = Math.max(0, Math.floor(xToTick(view, x) / bar) * bar);
            const clipId = createId('clp');
            dispatch({
              type: MidiCommands.addMidiClip.type,
              params: { clipId, trackId: track.id, name: `${track.name} part`, startTicks, durationTicks: bar },
            });
            selectClips([clipId], false);
          }}
          aria-label="Arrangement. Drag clips to move them, drag their edges to trim."
        />
        <div ref={playheadRef} className="timeline__playhead" />
        {isEmpty && !dragOver && (
          <div className="timeline__empty">
            <div>
              Drop audio or MIDI files here, or press <kbd>R</kbd> to record.
              <br />
              Everything stays on this computer.
            </div>
          </div>
        )}
        {dragOver && <div className="timeline__dropzone">Drop audio or a MIDI file to import</div>}
      </div>
    </div>
  );
}
